import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import {
  LayoutDashboard, Store, Users, FolderKanban, HardHat, Cpu,
  UsersRound, CalendarDays, Zap, Settings2, CreditCard,
  Bell, ScrollText, Settings, LogOut, UserCog, Radio, Menu, X
} from "lucide-react";
import logo from "../../logo-sagrada.png";

const navItems = [
  { labelKey: "nav.overview", icon: LayoutDashboard, path: "/" },
  { labelKey: "nav.resellers", icon: Store, path: "/revendeurs", adminOnly: true, platformOnly: true },
  { labelKey: "nav.clients", icon: Users, path: "/clients", adminOrRevendeur: true },
  { labelKey: "nav.projects", icon: FolderKanban, path: "/projets" },
  { labelKey: "nav.sites", icon: HardHat, path: "/chantiers", hideFor: ["client"] },
  { labelKey: "nav.nodes", icon: Cpu, path: "/noeuds", hideFor: ["client"], permission: "creerNoeud" },
  { labelKey: "nav.team", icon: UsersRound, path: "/equipe", adminOrRevendeur: true },
  { labelKey: "nav.events", icon: CalendarDays, path: "/evenements", permission: "configIoT" },
  { labelKey: "nav.automations", icon: Zap, path: "/automatisations", permission: "configIoT" },
  { labelKey: "nav.functions", icon: Settings2, path: "/fonctions", permission: "configIoT" },
  { labelKey: "nav.subscriptions", icon: CreditCard, path: "/abonnements", adminOrRevendeur: true },
  { labelKey: "nav.alerts", icon: Bell, path: "/alertes" },
  { labelKey: "nav.journal", icon: ScrollText, path: "/journal", permission: "configIoT" },
  { labelKey: "nav.users", icon: UserCog, path: "/utilisateurs", adminOnly: true, platformOnly: true },
  { labelKey: "nav.settings", icon: Settings, path: "/parametres" },
];

export default function Sidebar() {
  const { user, logout, canDo, adminMode, toggleAdminMode, canToggleAdmin } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const initials = user?.nom
    ? user.nom.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2)
    : "??";

  const sidebarContent = (
    <>
      {/* Logo */}
      <div className="p-5 border-b border-slate-700">
        <div className="flex items-center gap-3">
          <img src={logo} alt="Sagrada Soft" className="w-10 h-10 rounded-lg flex-shrink-0" />
          <div>
            <p className="font-bold text-white text-sm">SAGRADA SOFT</p>
            <p className="text-xs text-slate-400">{t('nav.platform')}</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {navItems.filter(item => {
          const isAdminRevendeurMode = user?.role === 'admin' && adminMode === 'revendeur';
          if (item.platformOnly && isAdminRevendeurMode) return false;
          if (item.adminOnly && user?.role !== 'admin') return false;
          if (item.adminOrRevendeur && !['admin', 'revendeur'].includes(user?.role)) return false;
          if (item.hideFor && item.hideFor.includes(user?.role)) return false;
          if (item.permission && !canDo(item.permission)) return false;
          return true;
        }).map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.path === "/"}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? "bg-blue-600 text-white"
                  : "text-slate-300 hover:bg-slate-800 hover:text-white"
              }`
            }
          >
            <item.icon size={18} />
            {t(item.labelKey)}
          </NavLink>
        ))}
      </nav>

      {/* Admin Mode Toggle */}
      {canToggleAdmin && (
        <div className="px-4 py-3 border-t border-slate-700">
          <div className="flex items-center justify-between bg-slate-800 rounded-lg p-2">
            <button
              onClick={() => adminMode !== 'revendeur' && toggleAdminMode()}
              className={`flex-1 text-center py-1.5 rounded-md text-xs font-medium transition-all ${
                adminMode === 'revendeur'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-slate-400 hover:text-slate-300'
              }`}
            >
              {t('nav.modeRevendeur', 'Revendeur')}
            </button>
            <button
              onClick={() => adminMode !== 'platform' && toggleAdminMode()}
              className={`flex-1 text-center py-1.5 rounded-md text-xs font-medium transition-all ${
                adminMode === 'platform'
                  ? 'bg-amber-500 text-white shadow-sm'
                  : 'text-slate-400 hover:text-slate-300'
              }`}
            >
              {t('nav.modePlatform', 'Platform')}
            </button>
          </div>
        </div>
      )}

      {/* User */}
      <div className="p-4 border-t border-slate-700">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-blue-600 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
            {initials}
          </div>
          <div className="overflow-hidden flex-1">
            <p className="text-sm font-medium text-white">{user?.nom || t('nav.user')}</p>
            <p className="text-xs text-slate-400 truncate">{user?.email || ""}</p>
          </div>
          <button
            onClick={handleLogout}
            className="p-1.5 rounded-lg hover:bg-slate-800 transition-colors text-slate-400 hover:text-white"
            title={t('nav.logout')}
          >
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile hamburger button — z élevé pour passer au-dessus des contrôles map Leaflet (z-1000) */}
      <button
        onClick={() => setMobileOpen(true)}
        className="md:hidden fixed top-4 left-4 z-[1100] p-2 bg-slate-900 text-white rounded-lg shadow-lg"
      >
        <Menu size={22} />
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 bg-black/50 z-[1110]" onClick={() => setMobileOpen(false)} />
      )}

      {/* Mobile sidebar (slide-in) */}
      <div className={`md:hidden fixed left-0 top-0 bottom-0 w-64 bg-slate-900 text-white flex flex-col z-[1120] transform transition-transform duration-200 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <button
          onClick={() => setMobileOpen(false)}
          className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white"
        >
          <X size={18} />
        </button>
        {sidebarContent}
      </div>

      {/* Desktop sidebar (always visible) */}
      <div className="hidden md:flex w-64 min-h-screen bg-slate-900 text-white flex-col fixed left-0 top-0 bottom-0 z-50">
        {sidebarContent}
      </div>
    </>
  );
}
